import type { ReactNode } from 'react'
import { useMediaQuery } from '../muiRexports'
import { useTheme } from './theme/moiaMuiTheme'

type AreaContentProps = {
  children: ReactNode
  className?: string
}

/**
 * Wraps the body of an area view.
 *
 * The app shell `AppLayout` owns the page height and scrolling: it gives every area a
 * bounded, scrollable slot below the top bar.
 * `AreaContent` grows to fill that slot and adds the standard responsive page padding.
 *
 * ## Usage
 * - Render your top bar (`MoiaTopBar` / `MoiaHighTopBar`) as a sibling *above*
 *   `AreaContent`.
 * - Put your body inside. A full-height `TableCard` fills it automatically;
 *   shorter content just grows and the slot scrolls when needed.
 * - To make a single child fill the slot (e.g. a `Card` wrapping a scroll
 *   area), give it `height: '100%'`. Do **not** compute your own
 *   `100vh` / `calc()` height — the shell owns the height, and viewport units
 *   ignore the bar and any shell inset, so they overshoot.
 */
export const AreaContent = ({ children, className }: AreaContentProps) => {
  const theme = useTheme()

  const isMobile = useMediaQuery(theme.breakpoints.down('md'))
  const defaultPadding = theme.space.Space4
  const responsivePadding = isMobile ? 0 : defaultPadding

  return (
    <div
      className={className}
      css={{
        display: 'flex',
        flexDirection: 'column',
        flexGrow: 1,
        flexBasis: 0,
        minHeight: 0,
        padding: responsivePadding,
      }}
    >
      {children}
    </div>
  )
}
